import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  getPreTemplateFailure,
  getPreTemplateSuccess,
  savePreTemplateFailure,
  savePreTemplateSuccess,
  getAssessmentsFailure,
  getAssessmentsSuccess,
  getPreHistoryFailure,
  getPreHistorySuccess,
  refreshCodeFailure,
  refreshCodeSuccess,
  verifyPinFailure,
  verifyPinSuccess,
  uploadDocFailure,
  uploadDocSuccess,
  saveAssessmentFailure,
  saveAssessmentSuccess,
  getAssessmentFailure,
  getAssessmentSuccess,
  updatePreStatusFailure,
  updatePreStatusSuccess,
  getUploadedDocsFailure,
  getUploadedDocsSuccess,
  getViewAssessmentFailure,
  getViewAssessmentSuccess,
  reprocessDocFailure,
  reprocessDocSuccess,
} from "./actions";
import {
  GET_PRE_TEMPLATE,
  SAVE_PRE_TEMPLATE,
  GET_ASSESSMENTS,
  GET_PRE_HISTORY,
  REFRESH_CODE,
  VERIFY_PIN,
  UPLOAD_DOC,
  SAVE_ASSESSMENT,
  GET_ASSESSMENT,
  UPDATE_PRE_STATUS,
  GET_UPLOADED_DOCS,
  GET_VIEW_ASSESSMENT,
  REPROCESS_DOC,
} from "./constants";
import QueryString from "query-string";

export function* getPreTemplateSaga({ payload, onGet }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/pre-assessment-template/${payload}`,
    );
    // API now sends preSections directly, fallback to parsing section_json for backward compatibility
    let tempSections =
      data?.preSections ??
      (data?.section_json ? JSON.parse(data.section_json) : []);
    yield put(
      getPreTemplateSuccess({
        preData: data,
        preSections: Array.isArray(tempSections) ? tempSections : [],
      }),
    );
    typeof onGet === "function" && onGet(tempSections);
  } catch (e) {
    yield put(getPreTemplateFailure());
  }
}

export function* savePreTemplateSaga({ payload, onSave }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/${payload?.locationId}/pre-assessment`,
      payload?.data,
    );
    yield put(savePreTemplateSuccess());
    yield put(successAlert("Pre Assessment Template saved successfully!"));
  } catch (e) {
    yield put(savePreTemplateFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while saving data!"));
  }
}

export function* getAssessmentsSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = QueryString.stringify(payload);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/pre-assessment-form?${query}`,
    );
    yield put(getAssessmentsSuccess(data));
  } catch (e) {
    yield put(getAssessmentsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while saving data!"));
  }
}

export function* getPreHistorySaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/pre-assessment-form-history/${payload}`,
    );
    yield put(getPreHistorySuccess(data));
  } catch (e) {
    yield put(getPreHistoryFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while saving data!"));
  }
}

export function* refreshCodeSaga({ payload, onRefresh }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/refresh-qr-pin/${payload}`,
    );
    yield put(refreshCodeSuccess());
    yield put(successAlert("Code refreshed successfully!"));
    typeof onRefresh === "function" && onRefresh(data);
  } catch (e) {
    yield put(refreshCodeFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while refreshing Code!"));
  }
}

export function* verifyPinSaga({ payload, onVerify }) {
  const token = process.env.REACT_APP_CUSTOMER_API_KEY;
  const { id, formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/qr-pin/validate/${id}`,
      formData,
    );
    yield put(verifyPinSuccess());
    typeof onVerify === "function" && onVerify(true);
  } catch (e) {
    typeof onVerify === "function" && onVerify(false);
    yield put(verifyPinFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while verifying PIN!"));
  }
}

export function* uploadDocSaga({ payload, onUpload }) {
  const { id, formData, isPrivate } = payload;
  const authToken = yield select((state) => state?.AuthReducer?.token);
  const staticToken = process.env.REACT_APP_CUSTOMER_API_KEY;
  const token = isPrivate ? authToken : staticToken;

  try {
    const { data } = yield call(
      api(token).post,
      isPrivate
        ? `/api/resident/upload-document-dashboard`
        : `/api/location/upload-document/public`,
      formData,
      { timeout: 500000 },
    );
    yield put(uploadDocSuccess());
    yield put(successAlert("Document processed successfully!"));
    typeof onUpload === "function" && onUpload(data);
  } catch (e) {
    typeof onUpload === "function" && onUpload(null);
    yield put(uploadDocFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while uploading documents!"));
  }
}

export function* saveAssessmentSaga({ payload, onCreate }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/pre-assessment-form`,
      payload,
    );

    yield put(saveAssessmentSuccess());
    yield put(getAssessmentSuccess(data));
    yield put(successAlert("Assessment saved successfully!"));
    typeof onCreate === "function" && onCreate(data);
  } catch (e) {
    yield put(saveAssessmentFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while saving assessment!"));
  }
}

export function* getAssessmentSaga({ payload, onGet }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/pre-assessment-form/${payload}`,
    );
    yield put(getAssessmentSuccess(data));
    typeof onGet === "function" && onGet(data);
  } catch (e) {
    yield put(getAssessmentFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching assessment!"));
  }
}

export function* updatePreStatusSaga({ payload, onUpdate }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/pre-assessment-form/status/${payload.id}`,
      payload,
    );

    yield put(updatePreStatusSuccess());
    yield put(successAlert("Assessment status updated successfully!"));
    typeof onUpdate === "function" && onUpdate(data);
  } catch (e) {
    yield put(updatePreStatusFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating assessment status!"));
  }
}

export function* getUploadedDocsSaga({ payload, onGet }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = QueryString.stringify(payload);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident/pre-assessment/uploaded-documents?${query}`,
    );
    yield put(getUploadedDocsSuccess(data));
    typeof onGet === "function" && onGet(data);
  } catch (e) {
    yield put(getUploadedDocsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching uploaded documents!"));
  }
}

export function* getViewAssessmentSaga({ payload, onGet }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident-template-response-details/${payload}`,
    );
    yield put(getViewAssessmentSuccess(data));
    typeof onGet === "function" && onGet(data);
  } catch (e) {
    yield put(getViewAssessmentFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching data!"));
  }
}

export function* reprocessDocSaga({ payload, onReprocess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/re-upload-document/${payload}`,
      { timeout: 10000000 },
    );
    yield put(reprocessDocSuccess(data));
    typeof onReprocess === "function" && onReprocess(data);
  } catch (e) {
    yield put(reprocessDocFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while reprocessing document!"));
  }
}

export function* getPreTemplateFlow() {
  yield takeEvery(GET_PRE_TEMPLATE, getPreTemplateSaga);
}
export function* savePreTemplateFlow() {
  yield takeEvery(SAVE_PRE_TEMPLATE, savePreTemplateSaga);
}
export function* getAssessmentsFlow() {
  yield takeEvery(GET_ASSESSMENTS, getAssessmentsSaga);
}
export function* getPreHistoryFlow() {
  yield takeEvery(GET_PRE_HISTORY, getPreHistorySaga);
}
export function* refreshCodeFlow() {
  yield takeEvery(REFRESH_CODE, refreshCodeSaga);
}
export function* verifyPinFlow() {
  yield takeEvery(VERIFY_PIN, verifyPinSaga);
}
export function* uploadDocFlow() {
  yield takeEvery(UPLOAD_DOC, uploadDocSaga);
}
export function* saveAssessmentFlow() {
  yield takeEvery(SAVE_ASSESSMENT, saveAssessmentSaga);
}
export function* getAssessmentFlow() {
  yield takeEvery(GET_ASSESSMENT, getAssessmentSaga);
}
export function* updatePreStatusFlow() {
  yield takeEvery(UPDATE_PRE_STATUS, updatePreStatusSaga);
}
export function* getUploadedDocsFlow() {
  yield takeEvery(GET_UPLOADED_DOCS, getUploadedDocsSaga);
}
export function* getViewAssessmentFlow() {
  yield takeEvery(GET_VIEW_ASSESSMENT, getViewAssessmentSaga);
}
export function* reprocessDocFlow() {
  yield takeEvery(REPROCESS_DOC, reprocessDocSaga);
}
export default function* ResidentPreAssessSaga() {
  yield all([
    getPreTemplateFlow(),
    savePreTemplateFlow(),
    getAssessmentsFlow(),
    getPreHistoryFlow(),
    refreshCodeFlow(),
    verifyPinFlow(),
    uploadDocFlow(),
    saveAssessmentFlow(),
    getAssessmentFlow(),
    updatePreStatusFlow(),
    getUploadedDocsFlow(),
    getViewAssessmentFlow(),
    reprocessDocFlow(),
  ]);
}
